import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  OHS_DIMENSIONS, 
  calculateOHS, 
  getHealthCategory, 
  checkAlertRules,
  mockDeptHealth,
  mockHistory
} from '../data/orgHealthLogic';

export const OrganizationalHealthScore = () => {
  const [currentScores] = useState({
    leadership: 48, safety: 42, collaboration: 55, culture: 50,
    conflict: 45, stress: 35, role: 52, comm: 45,
    trust: 48, engagement: 52, we: 58, change: 42
  });

  const previousOHS = 65;
  const currentOHS = useMemo(() => calculateOHS(currentScores), [currentScores]);
  const healthCategory = useMemo(() => getHealthCategory(currentOHS), [currentOHS]);
  const alerts = useMemo(() => checkAlertRules(currentOHS, previousOHS, currentScores), [currentOHS, currentScores]);

  const gaugeOption = {
    series: [{
      type: 'gauge',
      startAngle: 210,
      endAngle: -30,
      min: 0,
      max: 100,
      radius: '95%',
      axisLine: {
        lineStyle: {
          width: 12,
          color: [
            [0.4, '#f43f5e'],
            [0.6, '#fbbf24'],
            [0.75, '#f59e0b'],
            [0.9, '#10b981'],
            [1, '#06b6d4']
          ]
        }
      },
      pointer: { icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z', length: '12%', width: 20, offsetCenter: [0, '-55%'], itemStyle: { color: 'auto' } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      detail: { fontSize: 60, fontWeight: '900', offsetCenter: [0, '0%'], formatter: '{value}', color: 'inherit' },
      data: [{ value: currentOHS }]
    }]
  };

  const trendOption = {
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: mockHistory.map(h => h.date), axisLine: { show: false } },
    yAxis: { type: 'value', max: 100, splitLine: { lineStyle: { type: 'dashed' } } },
    series: [{
      data: mockHistory.map(h => h.score),
      type: 'line',
      smooth: true,
      color: '#6366f1',
      areaStyle: { opacity: 0.1 },
      symbolSize: 10,
      lineStyle: { width: 4 }
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Organizational Health Score</h1>
          <p className="text-slate-500 mt-1">Fullstendig strategisk analyse av virksomhetens sunnhetstilstand.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={<SafeIcon name="Calendar" />}>Trend 12 mnd</Button>
          <Button variant="primary" icon={<SafeIcon name="Download" />}>Styrerapport PDF</Button>
        </div>
      </div>

      {/* Main Score & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2 border-none shadow-2xl bg-white overflow-hidden relative group">
          <div className={`absolute top-0 right-0 w-64 h-64 bg-${healthCategory.color}-500/5 rounded-full blur-3xl -mr-20 -mt-20`} />
          <CardContent className="p-10 flex flex-col md:flex-row items-center gap-12 relative z-10">
            <div className="w-full md:w-1/2 h-72">
              <ReactECharts option={gaugeOption} style={{ height: '100%' }} />
            </div>
            <div className="w-full md:w-1/2 space-y-6">
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Helsestatus</p>
                <h2 className={`text-3xl font-black text-${healthCategory.color}-600 uppercase tracking-tight leading-tight mb-3`}>
                  {healthCategory.label}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed italic">
                  "{healthCategory.desc}"
                </p>
              </div>
              <div className="flex gap-4">
                <div className="flex-1 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Trend</p>
                  <div className="flex items-center gap-1 font-black text-rose-500">
                    <SafeIcon name="TrendingDown" /> -11%
                  </div>
                </div>
                <div className="flex-1 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Benchmark</p>
                  <div className="text-sm font-black text-slate-700">Under snitt</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Kritiske Varsler</h3>
          {alerts.map(alert => (
            <motion.div 
              key={alert.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`p-5 rounded-3xl border-l-4 shadow-sm bg-white border-${alert.severity === 'Critical' ? 'rose' : 'orange'}-500 flex gap-4`}
            >
              <div className={`w-12 h-12 rounded-2xl bg-${alert.severity === 'Critical' ? 'rose' : 'orange'}-50 text-${alert.severity === 'Critical' ? 'rose' : 'orange'}-600 flex items-center justify-center shrink-0 shadow-sm`}>
                <SafeIcon name="AlertTriangle" size={24} />
              </div>
              <div>
                <p className={`text-[9px] font-black uppercase text-${alert.severity === 'Critical' ? 'rose' : 'orange'}-600 mb-1`}>{alert.type}</p>
                <h4 className="text-sm font-bold text-slate-900 leading-tight">{alert.title}</h4>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{alert.desc}</p>
              </div>
            </motion.div>
          ))}
          <Card className="bg-indigo-600 text-white border-none">
            <CardContent className="p-6 space-y-3">
              <SafeIcon name="Zap" className="text-3xl text-indigo-300" />
              <h4 className="font-bold text-sm uppercase tracking-tight">AI Foreslått Tiltak</h4>
              <p className="text-xs text-indigo-100 leading-relaxed">
                Utviklingsavdelingen trenger umiddelbar lederintervensjon og ressurs-gjennomgang for å stabilisere OHS-scoren.
              </p>
              <Button size="sm" className="w-full bg-white text-indigo-600 border-none font-black text-[10px] py-3 uppercase tracking-widest">Start Intervensjon Plan</Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Breakdown Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Dimensjons-oversikt" subtitle="Veide skårer per kjerneområde" icon={<SafeIcon name="BarChart" className="text-indigo-600" />} />
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {OHS_DIMENSIONS.map(dim => (
                  <div key={dim.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-md transition-all group">
                    <div className="flex justify-between items-start mb-3">
                      <div className={`p-2 rounded-xl bg-${dim.color}-50 text-${dim.color}-600 group-hover:bg-${dim.color}-600 group-hover:text-white transition-colors shadow-sm`}>
                        <SafeIcon name={dim.icon} size={16} />
                      </div>
                      <span className="text-xs font-black text-slate-900">{currentScores[dim.id]}%</span>
                    </div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter truncate">{dim.name}</p>
                    <div className="mt-2 w-full bg-slate-200 h-1 rounded-full overflow-hidden">
                      <div className={`h-full bg-${dim.color}-500 transition-all duration-1000`} style={{ width: `${currentScores[dim.id]}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-slate-900 border-none text-white overflow-hidden">
              <CardHeader title="Styrerapport Innsikt" subtitle="Topp 5 Styrker & Risiko" />
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <h5 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Top 5 Strengths</h5>
                  {['Faglig engasjement', 'Operativt samarbeid', 'Rolle-klarhet (Adm)', 'Sykefraværsoppfølging', 'Innovasjonsvilje'].map((s, i) => (
                    <div key={i} className="flex gap-2 text-xs text-slate-300">
                      <SafeIcon name="Check" className="text-emerald-500 shrink-0" /> {s}
                    </div>
                  ))}
                </div>
                <div className="h-px bg-white/5" />
                <div className="space-y-3">
                  <h5 className="text-[10px] font-black text-rose-400 uppercase tracking-widest">Top 5 Risk Areas</h5>
                  {['Lederstøtte (Utvikling)', 'Psykologisk trygghet', 'Konfliktnivå (Salg)', 'Tidspress', 'Endringskommunikasjon'].map((r, i) => (
                    <div key={i} className="flex gap-2 text-xs text-slate-300">
                      <SafeIcon name="AlertTriangle" className="text-rose-500 shrink-0" /> {r}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader title="Avdelings-rangering" subtitle="Helse-index per enhet" icon={<SafeIcon name="BarChart" />} />
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {mockDeptHealth.map((dept, i) => (
                    <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-black text-slate-300">#{i+1}</span>
                        <span className="text-sm font-bold text-slate-700">{dept.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                          dept.status === 'Critical' ? 'bg-rose-50 text-rose-600' : 
                          dept.status === 'Weak' ? 'bg-orange-50 text-orange-600' : 
                          'bg-emerald-50 text-emerald-600'
                        }`}>
                          {dept.status}
                        </div>
                        <span className="text-sm font-black text-slate-900">{dept.score}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  <Button variant="ghost" size="sm" className="w-full text-[10px] font-black uppercase tracking-widest">Se fullstendig oversikt</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Helse-trend (OHS)" subtitle="Siste 12 måneder" icon={<SafeIcon name="TrendingUp" />} />
            <CardContent>
              <div className="h-64">
                <ReactECharts option={trendOption} style={{ height: '100%' }} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-emerald-600 text-white border-none shadow-xl shadow-emerald-100 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-16 -mt-16" />
            <CardContent className="p-8 space-y-6 relative z-10">
              <SafeIcon name="Award" className="text-4xl text-emerald-200" />
              <h3 className="text-xl font-black uppercase tracking-tight">ISO/Nordic Compliance</h3>
              <p className="text-xs text-emerald-50 leading-relaxed">
                Din nåværende helsescore kvalifiserer til <b>Sølv-sertifisering</b> i den nordiske standarden for arbeidsmiljø.
              </p>
              <Button variant="secondary" className="w-full bg-white text-emerald-600 border-none font-black text-[10px] py-4 uppercase tracking-widest">Gå til Sertifisering</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="AI Strategiske Råd" icon={<SafeIcon name="Cpu" className="text-indigo-600" />} />
            <CardContent className="space-y-4">
              {[
                { title: 'Lederutvikling', desc: 'Fokus på støttende ledelse i tekniske team.' },
                { title: 'Kulturbygging', desc: 'Workshop roadmap for økt psykologisk trygghet.' },
                { title: 'Oppfølgingsplan', desc: 'Ny kartlegging av Salg anbefales om 30 dager.' }
              ].map((rec, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <h6 className="text-[11px] font-bold text-slate-900">{rec.title}</h6>
                  <p className="text-[10px] text-slate-500 mt-0.5">{rec.desc}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};